/**
 * @module API
 * Cross-window iframe API contracts and client/server helpers.
 */

export type KeybanApiStatus = "operational" | "down";

import {
  type Application,
  type AuthMethod,
  type AuthUser,
  type CreateInvestIdentity,
  type DppClaimResult,
  type DppPassport,
  type DppProduct,
  type Hex,
  type InvestIdentity,
  type LoyaltyRewardTier,
  type Network,
} from "@keyban/types";

import { IFrameApiError, KeybanBaseError, SdkError } from "~/errors";

/**
 * Application service.
 */
export interface IApplicationService {
  /** Returns the current application. */
  getCurrent: () => Promise<Application>;
}

/**
 * Arguments for sending a One-Time Password (OTP).
 *
 * @example
 * // Email OTP
 * const args: AuthSendOtpArgs = {
 *   type: AuthMethod.EmailOtp,
 *   emailInputName: "email",
 * };
 *
 * @example
 * // Phone/SMS OTP
 * const args: AuthSendOtpArgs = {
 *   type: AuthMethod.PhoneOtp,
 *   phoneCallingCode: "+33",
 *   phoneInputName: "phone",
 * };
 */
export type AuthSendOtpArgs =
  | {
      type: typeof AuthMethod.EmailOtp;
      emailInputName: string;
    }
  | {
      type: typeof AuthMethod.PhoneOtp;
      phoneCallingCode: string;
      phoneInputName: string;
    };

/**
 * Arguments for signing in a user.
 */
export type AuthSignInArgs =
  | {
      type: typeof AuthMethod.EmailOtp;
      emailInputName: string;
      otpInputName: string;
    }
  | {
      type: typeof AuthMethod.PhoneOtp;
      phoneCallingCode: string;
      phoneInputName: string;
      otpInputName: string;
    }
  | { type: typeof AuthMethod.Google }
  | { type: typeof AuthMethod.Auth0 };

export type AuthUpdateUserArgs = {
  name: string;
  birthDate: string;
};

/**
 * Authentication service.
 *
 * @see {@link IKeybanApi}
 */
export interface IAuthService {
  /** Checks whether the current user is authenticated. */
  isAuthenticated: () => Promise<boolean>;
  /** Retrieves the authenticated user profile, or null. */
  getUser: () => Promise<AuthUser | null>;
  /** Sends an OTP to email or phone. */
  sendOtp: (args: AuthSendOtpArgs) => Promise<void>;
  /** Signs in a user using password or OTP. */
  signIn: (args: AuthSignInArgs) => Promise<AuthUser>;
  /** Signs out the current user. */
  signOut: () => Promise<void>;
  /** Updates the user profile. */
  updateUser: (args: AuthUpdateUserArgs) => Promise<AuthUser>;
}

/**
 * MPC signer for a given curve (ECDSA/EdDSA).
 */
export interface ISignerService {
  /** Performs a Distributed Key Generation ceremony, returning a client share. */
  dkg: () => Promise<string>;
  /** Signs a pre-hashed message with the server share and provided client share. */
  sign: (clientShare: string, message: string) => Promise<Hex>;
  /** Derives the public key associated with the provided client share. */
  publicKey: (clientShare: string) => Promise<Hex>;
}

/**
 * Account service.
 */
export interface IAccountService {
  /** Returns the on-chain address of the user account. */
  getAddress: () => Promise<string>;
  getId: () => Promise<string>;
}

/**
 * Storage for persisting and retrieving the MPC client share on the integrator side.
 */
export interface IClientShareStorageService {
  /** Retrieves a stored client share by key. */
  get: (key: string) => Promise<string | null>;
  /** Stores a client share under the given key. */
  set: (key: string, clientShare: string) => Promise<void>;
}

/**
 * Digital Product Passport (DPP) operations.
 *
 * Provides methods to retrieve product information and claim DPPs on the blockchain.
 * All claim operations return a transaction hash that can be used to track the
 * on-chain transaction status.
 * @example
 * ```typescript
 * // Retrieve a product sheet
 * const product = await dppService.getProduct("prod_123");
 * console.log(product.name, product.description);
 *
 * // Claim a DPP with explicit product sheet and DPP IDs
 * const { transactionHash } = await dppService.claim("prod_123", "dpp_456");
 * console.log("Transaction:", transactionHash);
 *
 * // Claim a DPP using a magic token (single-use claim link)
 * const result = await dppService.magicClaim("prod_123", "magic_xyz");
 * ```
 */
export interface IDppService {
  /**
   * Retrieves a single product sheet by ID.
   *
   * Returns comprehensive product information including name, description, images,
   * brand, country of origin, identifiers (GTIN, SKU), certification paths,
   * network status, and timestamps.
   * @param productId - Unique identifier of the product to retrieve
   * @returns Promise resolving to the product with complete product data
   * @throws {SdkError} If the product is not found or the user lacks permission
   * @example
   * ```typescript
   * const sheet = await dppService.getProduct("prod_abc123");
   * console.log("Product:", sheet.name, "by", sheet.brand);
   * console.log("Status:", sheet.status); // "ACTIVE", "ARCHIVED", "DRAFT", "UNLISTED"
   * ```
   */
  getProduct: (productId: string) => Promise<DppProduct>;

  /**
   * Retrieves a single passport by ID.
   *
   * @param passportId - Unique identifier of the passport to retrieve
   * @returns Promise resolving to the passport with complete passport data
   * @throws {SdkError} If the passport is not found or the user lacks permission
   * @example
   * ```typescript
   * const passport = await dppService.getPassport("prod_abc123");
   * console.log("Passport:", passport);
   * ```
   */
  getPassport: (passportId: string) => Promise<DppPassport>;

  /**
   * Claims a Digital Product Passport (DPP) on the blockchain.
   *
   * Associates a specific DPP instance with the authenticated user's account by
   * creating an on-chain claim transaction. The user must be authenticated and
   * authorized to claim the specified DPP.
   * @param productId - Product sheet identifier defining the product type
   * @param passportId - Specific DPP instance identifier to claim (unique serial number)
   * @returns Promise resolving to the claim result with transaction hash
   * @throws {SdkError} If authentication fails, DPP is already claimed, or transaction fails
   * @example
   * ```typescript
   * const result = await dppService.claim("prod_shirt_001", "dpp_serial_12345");
   * console.log("DPP claimed! Transaction:", result);
   * // transactionHash format: "0x..." (Ethereum) or similar depending on network
   * ```
   */
  claim: (productId: string, passportId: string) => Promise<DppClaimResult>;

  /**
   * Claims a Digital Product Passport using a single-use magic token.
   *
   * Simplified claim flow using a pre-authorized token (typically from a QR code
   * or claim link). The magic token embeds the DPP authorization, allowing claim
   * without explicitly specifying the DPP ID.
   * @param productId - Product sheet identifier for the product being claimed
   * @param magicToken - Single-use authorization token for claiming a specific DPP
   * @returns Promise resolving to the claim result with transaction hash
   * @throws {SdkError} If the token is invalid, expired, already used, or transaction fails
   * @example
   * ```typescript
   * // Magic token typically obtained from scanning QR code or clicking claim link
   * const magicToken = "*****";
   * const result = await dppService.magicClaim("prod_shirt_001", magicToken);
   * console.log("DPP claimed via magic link!", result);
   * ```
   */
  magicClaim: (
    productId: string,
    magicToken: string,
  ) => Promise<DppClaimResult>;
}

/**
 * Loyalty operations for wallet passes and tier retrieval.
 */
export interface ILoyaltyService {
  /** Returns the current loyalty optimistic balance. */
  getOptimisticBalance: () => Promise<string>;
  /** Returns the current loyalty reward tier, or null if none. */
  getRewardTier: () => Promise<LoyaltyRewardTier | null>;
  /** Returns a Google Wallet URL to add the pass. */
  addToGoogleWallet: () => Promise<string>;
  /** Adds an Apple Wallet pass. */
  addToAppleWallet: () => Promise<void>;
}

/**
 * Invest identity operations.
 */
export interface IInvestService {
  /** Returns the current user's invest identity, or null if not found. */
  getMe: () => Promise<InvestIdentity | null>;
  /** Creates a new invest identity. */
  create: (data: CreateInvestIdentity) => Promise<InvestIdentity>;
}

/**
 * Aggregated API surface exposed through the iframe transport.
 */

export interface IKeybanApi {
  application: IApplicationService;
  auth: IAuthService;
  ecdsa: ISignerService;
  eddsa: ISignerService;
  account: IAccountService;
  clientShareStorage: IClientShareStorageService;
  dpp: IDppService;
  loyalty: ILoyaltyService;
  invest: IInvestService;
}

/*
 * API internal types
 */

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyFn = (...args: any[]) => any;
type CastFn<T> = T extends AnyFn ? T : never;

type ServiceName = keyof IKeybanApi & string;
type MethodName<S extends ServiceName> = keyof IKeybanApi[S] & string;

type ClassMethod<S extends ServiceName, M extends MethodName<S>> = CastFn<
  IKeybanApi[S][M]
>;

type ApiCall<
  S extends ServiceName,
  M extends MethodName<S>,
  CM extends ClassMethod<S, M> = ClassMethod<S, M>,
> = {
  __KEYBAN_API: true;

  serviceName: S;
  methodName: M;
  args: Parameters<CM>;
};

type ApiResult<
  S extends ServiceName,
  M extends MethodName<S>,
  CM extends ClassMethod<S, M> = ClassMethod<S, M>,
> = [Awaited<ReturnType<CM>>, null] | [null, string];

/*
 * API server
 */

/**
 * @private
 */
export abstract class ApiServer implements IKeybanApi {
  abstract application: IApplicationService;
  abstract auth: IAuthService;
  abstract ecdsa: ISignerService;
  abstract eddsa: ISignerService;
  abstract account: IAccountService;
  abstract loyalty: ILoyaltyService;
  abstract clientShareStorage: IClientShareStorageService;
  abstract dpp: IDppService;
  abstract invest: IInvestService;

  constructor() {
    window.addEventListener("message", this.#handleMessage);
  }

  // Forced validation of service's methods, see listener below
  static #definitions: {
    [S in ServiceName]: {
      [M in MethodName<S>]: IKeybanApi[S][M] extends AnyFn ? true : never;
    };
  } = {
    application: {
      getCurrent: true,
    },
    auth: {
      isAuthenticated: true,
      getUser: true,
      sendOtp: true,
      signIn: true,
      signOut: true,
      updateUser: true,
    },
    ecdsa: {
      dkg: true,
      sign: true,
      publicKey: true,
    },
    eddsa: {
      dkg: true,
      sign: true,
      publicKey: true,
    },
    account: {
      getAddress: true,
      getId: true,
    },
    loyalty: {
      getOptimisticBalance: true,
      getRewardTier: true,
      addToGoogleWallet: true,
      addToAppleWallet: true,
    },
    dpp: {
      getProduct: true,
      getPassport: true,
      claim: true,
      magicClaim: true,
    },
    clientShareStorage: {
      get: true,
      set: true,
    },
    invest: {
      getMe: true,
      create: true,
    },
  };

  abstract checkEventOrigin(eventOrigin: string): Promise<boolean>;

  #handleMessage = async <S extends ServiceName, M extends MethodName<S>>(
    event: MessageEvent<ApiCall<S, M>>,
  ) => {
    if (!event.data.__KEYBAN_API) return;

    try {
      // Check the message originated from an allowed domain for this
      // specific application.
      const originAllowed = await this.checkEventOrigin(event.origin);
      if (!originAllowed)
        throw new IFrameApiError(
          IFrameApiError.types.InvalidOrigin,
          "ApiServer",
        );

      const { serviceName, methodName } = event.data;

      // An attacker could possibly try to call a method on the service
      // object that is not intended to be exposed. This  ensures the
      // method is effectively allowed.
      if (!ApiServer.#definitions[serviceName]?.[methodName])
        throw new IFrameApiError(
          IFrameApiError.types.InvalidCall,
          `ApiServer:${serviceName}.${methodName}`,
        );

      const fn = this[serviceName]?.[methodName] as ClassMethod<S, M>;
      if (!fn)
        throw new IFrameApiError(
          IFrameApiError.types.InvalidCall,
          `ApiServer:${serviceName}.${methodName}`,
        );

      const result = await fn.apply(this[serviceName], event.data.args);

      event.ports[0].postMessage([result, null]);
    } catch (error) {
      let err = error;

      if (!(err instanceof KeybanBaseError)) {
        console.error(error);
        err = new SdkError(
          SdkError.types.UnknownIframeApiError,
          "ApiServer",
          error as Error,
        );
      }

      event.ports[0].postMessage([null, JSON.stringify(err)]);
    }
  };

  static openPopup(url: URL | string) {
    const channel = new MessageChannel();

    return new Promise<void>((resolve) => {
      channel.port1.onmessage = () => {
        channel.port1.close();
        resolve();
      };

      const message = {
        type: "keyban:popup",
        url: url.toString(),
      };

      window.parent.postMessage(message, "*", [channel.port2]);
    });
  }
}

/*
 * API client
 */

type ApiClientOptions = {
  apiUrl: URL | string;
  appId: string;
  network: Network;
};

/**
 * @private
 */
export class ApiClient {
  #iframeUrl: URL;
  #iframe: Promise<HTMLIFrameElement>;

  static #instances: Map<string, ApiClient> = new Map();

  static #getIframeUrl({ apiUrl, appId, network }: ApiClientOptions) {
    const iframeUrl = new URL("/sdk-client/api", apiUrl);
    iframeUrl.searchParams.set("appId", appId);
    iframeUrl.searchParams.set("network", network);
    return iframeUrl;
  }

  /**
   * Returns a shared instance per unique iframe URL.
   *
   * @param options - API client options.
   * @returns ApiClient instance bound to the provided options.
   */
  static getInstance(options: ApiClientOptions): ApiClient {
    const key = ApiClient.#getIframeUrl(options).toString();
    if (!ApiClient.#instances.has(key))
      ApiClient.#instances.set(key, new ApiClient(options));

    return ApiClient.#instances.get(key)!;
  }

  api: IKeybanApi;

  private constructor(options: ApiClientOptions) {
    this.#iframeUrl = ApiClient.#getIframeUrl(options);

    window.addEventListener("message", this.#popupHandler);

    this.#iframe = new Promise((resolve, reject) => {
      const iframe = Object.assign(document.createElement("iframe"), {
        src: this.#iframeUrl,
        hidden: true,
        onload: () => resolve(iframe),
        onerror: reject,
      });

      document.body.appendChild(iframe);
    });

    this.api = new Proxy({} as IKeybanApi, {
      // API Proxy
      get: <S extends ServiceName>(_target: object, serviceName: S) =>
        new Proxy({} as IKeybanApi[S], {
          // Service Proxy
          get: <M extends MethodName<S>>(_target: object, methodName: M) =>
            new Proxy(() => {}, {
              // Method Proxy
              apply: <CM extends ClassMethod<S, M>>(
                _target: () => unknown,
                _thisArg: object,
                args: Parameters<CM>,
              ) => this.#call(serviceName, methodName, args),
            }),
        }),
    });
  }

  #popupHandler = async (event: MessageEvent) => {
    const iframe = await this.#iframe;
    if (event.source !== iframe.contentWindow) return;
    if (typeof event.data !== "object") return;
    if (event.data.type != "keyban:popup") return;

    const width = 500;
    const height = 685;
    const left = window.screenX + (window.innerWidth - width) / 2;
    const top = window.screenY + (window.innerHeight - height) / 2;

    const popup = window.open(
      event.data.url,
      `keyban:popup`,
      `left=${left},top=${top},width=${width},height=${height},popup,resizable,scrollbars`,
    );

    // Wait for popup to be closed
    await new Promise<void>((resolve) => {
      const interval = setInterval(() => {
        if (!popup?.closed) return;

        clearInterval(interval);
        resolve();
      }, 100);
    });

    event.ports[0].postMessage("closed");
  };

  async #call<
    S extends ServiceName,
    M extends MethodName<S>,
    CM extends ClassMethod<S, M> = ClassMethod<S, M>,
  >(
    serviceName: S,
    methodName: M,
    args: Parameters<CM>,
  ): Promise<Awaited<ReturnType<CM>>> {
    const iframe = await this.#iframe;
    const channel = new MessageChannel();

    return await new Promise((resolve, reject) => {
      channel.port1.onmessage = ({
        data: [result, error],
      }: MessageEvent<ApiResult<S, M>>) => {
        channel.port1.close();
        return error != null
          ? reject(new KeybanBaseError(JSON.parse(error)))
          : resolve(result);
      };

      const message: ApiCall<S, M> = {
        __KEYBAN_API: true,
        serviceName,
        methodName,
        args,
      };

      iframe.contentWindow?.postMessage(message, this.#iframeUrl.origin, [
        channel.port2,
      ]);
    });
  }
}
